import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaGithub, 
  FaExternalLinkAlt, 
  FaArrowLeft,
  FaCode,
  FaCalendarAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaLightbulb,
  FaRoad,
  FaTag,
  FaUser
} from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const ProjectDetail = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await apiService.getProjectBySlug(slug);
        setProject(response.data);
      } catch (error) {
        console.error('Error fetching project:', error);
        // Fallback data
        setProject({
          id: 1,
          title: 'HealthGuardX',
          slug: 'healthguardx',
          description: 'Decentralized Healthcare Identity Platform using blockchain and IPFS',
          content: `
## Problem
Traditional healthcare systems face significant challenges with data security, patient privacy, and interoperability. Patients lack control over their medical data, and healthcare providers struggle with fragmented systems.

## Solution
HealthGuardX is a decentralized healthcare identity platform that gives patients secure ownership and control of their medical records using blockchain technology and decentralized storage.

## Architecture
The platform combines:
- **Blockchain (Solana)**: For identity verification and audit trails
- **IPFS**: For decentralized storage of medical documents
- **Wallet-based authentication**: For secure access control
- **Role-based access**: For different user types (patients, doctors, administrators)

## Key Features
- Blockchain-backed medical identity
- Secure patient document management
- Audit trail for record integrity
- Role-based access control
- Decentralized storage via IPFS
- Wallet-based authentication

## Technologies Used
- Solidity for smart contracts
- React with TypeScript for frontend
- Solana blockchain
- IPFS for storage
- Tailwind CSS for styling

## Challenges
- Ensuring data privacy while maintaining transparency
- Managing blockchain transaction costs
- User experience for non-technical users

## Lessons Learned
- The importance of user-centric design in blockchain applications
- Balancing decentralization with usability
- Managing gas costs effectively
          `,
          tech_stack: ['Solidity', 'React', 'TypeScript', 'Solana', 'IPFS', 'Tailwind CSS'],
          features: [
            'Blockchain-backed medical identity',
            'Role-based access control',
            'Secure patient document management',
            'Audit trail for record integrity',
            'Decentralized storage via IPFS',
            'Wallet-based authentication'
          ],
          challenges: [
            'Ensuring HIPAA compliance',
            'Managing blockchain transaction costs',
            'User experience for non-technical users',
            'Data privacy while maintaining transparency'
          ],
          lessons: [
            'Importance of user-centric design in blockchain applications',
            'Balancing decentralization with usability',
            'Managing gas costs effectively',
            'Need for clear user education'
          ],
          roadmap: [
            'Integration with more healthcare providers',
            'Mobile app development',
            'AI-powered health insights',
            'Telemedicine integration',
            'Cross-chain compatibility'
          ],
          github_url: 'https://github.com/developer13-art/HealthGuard',
          live_url: 'https://healthguard-1-pba4.onrender.com',
          category: 'blockchain',
          featured: true,
          created_at: '2024-01-15T10:00:00Z'
        });
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl text-white mb-2">Project Not Found</h2>
          <Link to="/projects" className="text-primary-400 hover:text-primary-300">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link to="/projects" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-300 mb-6">
          <FaArrowLeft size={14} />
          Back to Projects
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-2">
                {project.category && (
                  <span className="px-3 py-1 rounded-full bg-primary-500/20 border border-primary-500/20 text-primary-400 text-sm">
                    {project.category}
                  </span>
                )}
                {project.featured && (
                  <span className="px-3 py-1 rounded-full bg-yellow-500/20 border border-yellow-500/20 text-yellow-400 text-sm">
                    Featured
                  </span>
                )}
              </div>
              <h1 className="text-4xl font-bold text-white">{project.title}</h1>
              <p className="text-gray-400 mt-2 text-lg">{project.description}</p>
            </div>
          </div>

          {/* Links */}
          <div className="flex flex-wrap gap-3 mt-4">
            {project.github_url && (
              <a 
                href={project.github_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 text-gray-400 hover:text-white"
              >
                <FaGithub />
                GitHub
              </a>
            )}
            {project.live_url && (
              <a 
                href={project.live_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500/20 hover:bg-primary-500/30 transition-all duration-300 text-primary-400"
              >
                <FaExternalLinkAlt />
                Live Demo
              </a>
            )}
          </div>
        </motion.div>

        {/* Tech Stack */}
        {project.tech_stack && project.tech_stack.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-2xl p-6 mb-6"
          >
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FaCode className="text-primary-400" />
              Tech Stack
            </h2>
            <div className="flex flex-wrap gap-2">
              {project.tech_stack.map((tech, i) => (
                <span 
                  key={i}
                  className="px-3 py-1 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm"
                >
                  {tech}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        {/* Content */}
        {project.content && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-2xl p-6 mb-6"
          >
            <h2 className="text-lg font-semibold text-white mb-4">Overview</h2>
            <div className="text-gray-300 leading-relaxed prose prose-invert max-w-none">
              {project.content.split('\n').map((paragraph, i) => {
                if (paragraph.startsWith('## ')) {
                  return <h3 key={i} className="text-xl font-semibold text-white mt-6 mb-3">{paragraph.substring(3)}</h3>;
                } else if (paragraph.startsWith('- ')) {
                  return <li key={i} className="ml-4 text-gray-300">{paragraph.substring(2)}</li>;
                } else if (paragraph.startsWith('**')) {
                  return <strong key={i} className="text-white">{paragraph.replace(/\*\*/g, '')}</strong>;
                } else if (paragraph.trim()) {
                  return <p key={i} className="mb-3">{paragraph}</p>;
                }
                return null;
              })}
            </div>
          </motion.div>
        )}

        {/* Features */}
        {project.features && project.features.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass rounded-2xl p-6 mb-6"
          >
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FaCheckCircle className="text-green-500" />
              Key Features
            </h2>
            <ul className="space-y-2">
              {project.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-300">
                  <FaCheckCircle className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Challenges & Lessons */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {project.challenges && project.challenges.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="glass rounded-2xl p-6"
            >
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FaExclamationTriangle className="text-yellow-500" />
                Challenges
              </h2>
              <ul className="space-y-2">
                {project.challenges.map((challenge, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-300">
                    <span className="text-yellow-500 mt-0.5">•</span>
                    <span>{challenge}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {project.lessons && project.lessons.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="glass rounded-2xl p-6"
            >
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FaLightbulb className="text-purple-500" />
                Lessons Learned
              </h2>
              <ul className="space-y-2">
                {project.lessons.map((lesson, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-300">
                    <span className="text-purple-500 mt-0.5">•</span>
                    <span>{lesson}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </div>

        {/* Roadmap */}
        {project.roadmap && project.roadmap.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="glass rounded-2xl p-6 mb-6"
          >
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FaRoad className="text-primary-400" />
              Future Roadmap
            </h2>
            <ul className="space-y-2">
              {project.roadmap.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-300">
                  <span className="text-primary-400 mt-0.5">→</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Project Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="glass rounded-2xl p-6"
        >
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
            {project.created_at && (
              <span className="flex items-center gap-1.5">
                <FaCalendarAlt className="text-xs" />
                Created: {new Date(project.created_at).toLocaleDateString()}
              </span>
            )}
            {project.view_count && (
              <span className="flex items-center gap-1.5">
                <FaUser className="text-xs" />
                {project.view_count} views
              </span>
            )}
            {project.github_url && (
              <a 
                href={project.github_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-primary-400 hover:text-primary-300 transition-colors duration-300"
              >
                <FaGithub className="text-xs" />
                View on GitHub
              </a>
            )}
            {project.live_url && (
              <a 
                href={project.live_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-primary-400 hover:text-primary-300 transition-colors duration-300"
              >
                <FaExternalLinkAlt className="text-xs" />
                Live Demo
              </a>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectDetail;